import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, BadgeCheck } from "lucide-react";
import { PROOF_IMAGES } from "../lib/site";
import { SectionHead, Reveal } from "./Reveal";

export default function IncomeProof() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  const loop = [...PROOF_IMAGES, ...PROOF_IMAGES, ...PROOF_IMAGES];

  return (
    <section id="proof" data-testid="proof-section" className="grain relative overflow-hidden bg-[#100D18] py-20 sm:py-28">
      <div className="drift pointer-events-none absolute -left-24 top-1/3 h-80 w-80 rounded-full bg-[#7C3AED]/15 blur-[120px]" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
        <SectionHead
          dark
          eyebrow="Real Work. Real Experience."
          title={<>My Business & <span className="bg-gradient-to-r from-[#C9B8F5] to-[#9B5CF0] bg-clip-text text-transparent">Income Proof.</span></>}
          sub="A look at my personal business journey and selected results."
          testId="proof"
        />
      </div>

      <Reveal delay={0.2} className="mt-14">
        <div className="marquee relative overflow-hidden" data-testid="income-proof-marquee">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-[#100D18] to-transparent sm:w-28" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-[#100D18] to-transparent sm:w-28" />
          <div className="marquee-track flex w-max gap-5 px-5">
            {loop.map((img, i) => (
              <button
                key={`${img.src}-${i}`}
                type="button"
                onClick={() => setActive(img)}
                data-testid={i < PROOF_IMAGES.length ? `proof-card-${i}` : undefined}
                aria-label={img.caption}
                className="group flex w-fit shrink-0 flex-col items-start"
              >
                <img
                  src={img.src}
                  alt={img.caption}
                  loading="lazy"
                  className="block h-[300px] w-auto rounded-2xl border border-[#39235D] transition-[border-color,transform,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:border-[#9B5CF0]/60 group-hover:shadow-[0_20px_45px_-15px_rgba(155,92,240,0.4)] sm:h-[340px]"
                />
                <div className="mt-3 flex w-full max-w-[160px] items-start gap-2 px-0.5 text-left sm:max-w-[175px]">
                  <BadgeCheck size={14} className="mt-0.5 shrink-0 text-[#9B5CF0]" />
                  <span className="text-xs font-semibold leading-snug text-[#B8B1C8]">
                    {img.caption}
                    {img.date ? ` • ${img.date}` : ""}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </Reveal>

      <div className="relative mx-auto mt-10 max-w-6xl px-5 sm:px-8">
        <p className="text-center text-xs italic text-[#777181]" data-testid="proof-disclaimer">
          Individual results vary. Past earnings do not guarantee future income.
        </p>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-5 backdrop-blur-sm"
            onClick={() => setActive(null)}
            data-testid="proof-lightbox"
          >
            <button
              type="button"
              onClick={() => setActive(null)}
              aria-label="Close"
              data-testid="proof-lightbox-close"
              className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <X size={20} />
            </button>
            <motion.figure
              initial={{ scale: 0.92, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 8 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.src} alt={active.caption} className="max-h-[82vh] w-auto rounded-2xl border border-[#39235D]" />
              <figcaption className="mt-4 text-center text-sm font-semibold text-[#B8B1C8]">
                {active.caption}
                {active.date ? ` • ${active.date}` : ""}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
